import React, { useRef, useState, useEffect } from "react";
import SubHeader from "../customComponents/SubHeader";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { showAlert } from "../../redux/alertSlice";
import api from "../../utils/axiosInstance";
import moment from "moment";
import GroupedStockReportTable from "../customComponents/tables/GroupedStockReportTable";

const tableConfig = [
  {
    key: "itemName",
    label: "Item Name",
    width: "35%",
    align: "left",
    dataKey: "itemName",
  },
  {
    key: "headName",
    label: "Head Name",
    width: "35%",
    align: "left",
    dataKey: "headName",
  },
  {
    key: "required",
    label: "Required.",
    width: "15%",
    align: "right",
    dataKey: "required",
  },
  {
    key: "available",
    label: "Available",
    width: "15%",
    align: "right",
    dataKey: "available",
  },
];
const DummyBranchOptions = [
  { name: "Shastri Nagar", city: "Meerut", state: "Uttar Pradesh" },
  { name: "Ganga Nagar", city: "Meerut", state: "Uttar Pradesh" },
  { name: "Modipuram", city: "Meerut", state: "Uttar Pradesh" },
];

const GroupedStockReport = ({ toggleMobileMenu }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [data, setData] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [reportData, setReportData] = useState({ tableData: [], totals: {} });

  const searchBoxRef = useRef();
  const debounceTimeout = useRef(null); // Ref to store the timeout

  // Updated formData with new filters
  const [formData, setFormData] = useState({
    asOnDate: moment(new Date()).format("YYYY-MM-DD"),
    type: "", // "G" or "D"
    branch: "", // Branch Name
  });

  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Example getCustomerList API (for customer autocomplete search)
  const getCustomerList = async (value) => {
    try {
      const response = await api.get(
        `/customers/get-all-customers?search=${value}`
      );
      if (response.status === 200) {
        const list = response.data;
      }
    } catch (error) {
      console.log("Error while loading customers " + error);
    }
  };
  const getBranch = async (all, search) => {
    try {
      setLoading(true);
      const response = await api.post("/user/erp-data");
      if (response.data.success) {
        setBranchOptions(response.data.data.webidInfo);
        setLoading(false);
      } else {
        setBranchOptions([]);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerData = async () => {
    if (!formData.type || !formData.webid) {
      dispatch(
        showAlert({
          message: "Select a Type and Branch before fetching data",
          severity: "warning",
        })
      );
      return;
    }
    try {
      setLoading(true);
      // Send API request with the three filters
      const response = await api.post("/branch-reports/get-grouped-stock-report", {
        asOnDate: formData.asOnDate,
        type: formData.type,
        webid: formData.webid,
      });

      if (response.data?.success) {
        setStatement(response.data.data);
        setReportData({
          tableData: response.data.data,
          totals: response.data.totals,
        });
      } else {
        setStatement([]);
        setReportData({ tableData: [], totals: {} });
        dispatch(
          showAlert({
            message: response.data.message || "No data found",
            severity: "warning",
          })
        );
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      dispatch(
        showAlert({ message: "Error fetching data", severity: "error" })
      );
    }
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    if (value && value !== "") {
      setInputValue(value);

      // Debouncing logic
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        getCustomerList(value);
      }, 1000); // 1-second delay
    }
  };

  const handleOptionSelect = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //   // Optional helper function to reset filters if you want to add that later
  //   const handleClearForm = () => {
  //     setFormData({
  //       asOnDate: moment(new Date()).format("YYYY-MM-DD"),
  //       type: "",
  //       branch: "",
  //     });
  //     setStatement([]);
  //     setSelectedValue(null);
  //     setInputValue("");
  //   };

  useEffect(() => {
      getBranch();
    }, []);

  return (
    <>
      <SubHeader
        text={"Grouped Stock Report"}
        toggleMobileMenu={toggleMobileMenu}
      />
      <div className="w-full max-w-[1100px] mx-auto px-4 lg:px-0">
        <div className="relative w-full lg:min-h-[550px] rounded-[10px] mt-8 mb-2 shadow-custom-dark">
          <div className="w-full py-4 lg:pt-6 px-4 lg:px-8 ">
            <div>
              <div className="font-mont font-semibold pb-2">
                Filters
              </div>

              {/* Filters Row */}
              <div className="flex flex-col lg:flex-row px-0 lg:px-2 gap-4 lg:gap-6 lg:items-end mt-4 lg:mt-0">
                {/* As on Date */}
                <TextField
                  sx={{ width: { xs: "100%", lg: "30%" } }}
                  value={formData.asOnDate}
                  label="As on Date"
                  variant="standard"
                  type="date"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      asOnDate: e.target.value,
                    }))
                  }
                />

                {/* Type (G/D) */}
                <TextField
                  select
                  label="Type"
                  variant="standard"
                  SelectProps={{ native: true }}
                  sx={{ width: { xs: "100%", lg: "20%" } }}
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, type: e.target.value }))
                  }
                >
                  <option value=""></option>
                  <option value="G">Gold</option>
                  <option value="D">Diamond</option>
                </TextField>

                {/* Branch Name */}
                <div className="lg:w-[240px]">
                  <Autocomplete
                    size="small"
                    freeSolo
                    autoHighlight
                    inputValue={formData?.branchName || ""}
                    onInputChange={(event, newInputValue) => {
                      handleOptionSelect("branchName", newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      handleOptionSelect("webid", newValue?.webid);
                    }}
                    options={branchOptions}
                    getOptionLabel={(option) => {
                      return option.accname;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoComplete="text"
                        label="Branch."
                        inputRef={searchBoxRef}
                      />
                    )}
                  />
                </div>
                {/* <TextField
                  label="Branch Name"
                  variant="standard"
                  sx={{ width: { xs: "100%", lg: "30%" } }}
                  value={formData.branch}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, branch: e.target.value }))
                  }
                /> */}
              <div className="flex justify-start mt-6">
                <button
                  className="w-full lg:w-auto py-2 px-3 border bg-sixth text-white text-sm font-mont font-semibold rounded-[5px] hover:bg-sixth/80"
                  disabled={loading}
                  onClick={getCustomerData}
                >
                  {loading ? "Loading..." : "Get Data"}
                </button>
              </div>
              </div>

 
            </div>
          </div>

          {/* Results Table */}
          <div className="w-full px-4 lg:px-7 pb-6">
            <GroupedStockReportTable
              tableData={reportData ? reportData.tableData : []}
              totals={reportData ? reportData.totals : {}}
              loading={loading}
              columns={tableConfig}
              searchFields={["itemName", "headName"]}
              reportType={formData.type}
              webid={formData.webid}
              asOnDate={formData.asOnDate}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupedStockReport;
